import type { ReactNode } from 'react'

import { usePermissions } from '../hooks/usePermissions'
import type { Permission } from '../permissions/permissions'

interface CanProps {
  /** Permiso requerido. */
  permission?: Permission
  /** Alternativa: basta con tener uno de estos permisos. */
  anyOf?: readonly Permission[]
  /** Alternativa: hay que tener todos estos permisos. */
  allOf?: readonly Permission[]
  /** Qué mostrar si NO tiene permiso. Por defecto, nada. */
  fallback?: ReactNode
  children: ReactNode
}

/**
 * Muestra su contenido solo si el usuario tiene el permiso indicado.
 *
 * Es la forma declarativa de aplicar permisos en el JSX: se lee de corrido
 * y evita llenar los componentes de `if` anidados.
 *
 * @example
 * <Can permission={PERMISSIONS.CUSTOMERS_CREATE}>
 *   <Button>Nuevo cliente</Button>
 * </Can>
 *
 * @example Con mensaje alternativo
 * <Can permission={PERMISSIONS.REPORTS_FINANCIAL_VIEW} fallback={<NoAccess />}>
 *   <FinancialReport />
 * </Can>
 *
 * ⚠️ Esto oculta la interfaz, NO protege los datos. El backend debe validar
 * los mismos permisos: cualquiera puede modificar lo que corre en su navegador.
 */
export function Can({ permission, anyOf, allOf, fallback = null, children }: CanProps) {
  const { can, canAny, canAll } = usePermissions()

  const authorized =
    (permission !== undefined && can(permission)) ||
    (anyOf !== undefined && canAny(anyOf)) ||
    (allOf !== undefined && canAll(allOf))

  return authorized ? <>{children}</> : <>{fallback}</>
}
